import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SectionHeader } from "../shared/SectionHeader";
import { ToggleRow } from "../shared/ToggleRow";
import { SettingsInput, labelClass } from "../shared/SettingsInput";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Prefs = {
  low_stock_enabled: boolean;
  low_stock_threshold_days: number;
  dead_stock_enabled: boolean;
  dead_stock_sensitivity: number;
  whatsapp_summary_enabled: boolean;
  whatsapp_summary_phone: string | null;
  subscription_reminder_enabled: boolean;
  subscription_reminder_days: number;
  team_join_enabled: boolean;
  ai_analysis_ready_enabled: boolean;
  reorder_alert_enabled: boolean;
};

const defaultPrefs: Prefs = {
  low_stock_enabled: true,
  low_stock_threshold_days: 7,
  dead_stock_enabled: true,
  dead_stock_sensitivity: 30,
  whatsapp_summary_enabled: false,
  whatsapp_summary_phone: null,
  subscription_reminder_enabled: true,
  subscription_reminder_days: 7,
  team_join_enabled: true,
  ai_analysis_ready_enabled: true,
  reorder_alert_enabled: true,
};

export function NotificationsAlerts() {
  const { user, businessId } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(defaultPrefs);

  useEffect(() => {
    if (!user?.id || !businessId) return;
    supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", user.id)
      .eq("business_id", businessId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          const d = data as Record<string, unknown>;
          setPrefs({
            low_stock_enabled: d.low_stock_enabled as boolean ?? true,
            low_stock_threshold_days: (d.low_stock_threshold_days as number) ?? 7,
            dead_stock_enabled: d.dead_stock_enabled as boolean ?? true,
            dead_stock_sensitivity: (d.dead_stock_sensitivity as number) ?? 30,
            whatsapp_summary_enabled: d.whatsapp_summary_enabled as boolean ?? false,
            whatsapp_summary_phone: (d.whatsapp_summary_phone as string) ?? null,
            subscription_reminder_enabled: d.subscription_reminder_enabled as boolean ?? true,
            subscription_reminder_days: (d.subscription_reminder_days as number) ?? 7,
            team_join_enabled: d.team_join_enabled as boolean ?? true,
            ai_analysis_ready_enabled: d.ai_analysis_ready_enabled as boolean ?? true,
            reorder_alert_enabled: d.reorder_alert_enabled as boolean ?? true,
          });
        }
      });
  }, [user?.id, businessId]);

  const upsert = async (updates: Partial<Prefs>) => {
    if (!user?.id || !businessId) return;
    const row = {
      business_id: businessId,
      user_id: user.id,
      ...prefs,
      ...updates,
      updated_at: new Date().toISOString(),
    };
    await supabase.from("notification_preferences").upsert(row, {
      onConflict: "business_id,user_id",
    });
  };

  return (
    <>
      <SectionHeader
        title="Notifications & Alerts"
        description="Choose how you want to be notified."
      />
      <div className="space-y-0">
        <ToggleRow
          label="Low Stock Alerts"
          description="Get notified when a product drops below its minimum threshold"
          checked={prefs.low_stock_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, low_stock_enabled: v }))}
          onSave={(v) => upsert({ low_stock_enabled: v })}
          children={
            <div className="flex items-center gap-2">
              <Label className={labelClass}>Alert when stock runway ≤</Label>
              <Select
                value={String(prefs.low_stock_threshold_days)}
                onValueChange={(v) => {
                  const n = parseInt(v, 10);
                  setPrefs((p) => ({ ...p, low_stock_threshold_days: n }));
                  upsert({ low_stock_threshold_days: n });
                }}
              >
                <SelectTrigger className="w-32 bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        />
        <ToggleRow
          label="Dead Stock Alerts"
          description="Notify when a product has had no sales within a set period"
          checked={prefs.dead_stock_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, dead_stock_enabled: v }))}
          onSave={(v) => upsert({ dead_stock_enabled: v })}
          children={
            <Select
              value={String(prefs.dead_stock_sensitivity)}
              onValueChange={(v) => {
                const n = parseInt(v, 10);
                setPrefs((p) => ({ ...p, dead_stock_sensitivity: n }));
                upsert({ dead_stock_sensitivity: n });
              }}
            >
              <SelectTrigger className="w-40 bg-background border-border">
                <SelectValue placeholder="No sales in" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
              </SelectContent>
            </Select>
          }
        />
        <ToggleRow
          label="Daily WhatsApp Summary"
          description="Receive a morning summary of yesterday's sales, alerts, and health score"
          checked={prefs.whatsapp_summary_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, whatsapp_summary_enabled: v }))}
          onSave={(v) => upsert({ whatsapp_summary_enabled: v })}
          children={
            <div className="space-y-2">
              <SettingsInput
                placeholder="+260 97 000 0000"
                value={prefs.whatsapp_summary_phone ?? ""}
                onChange={(e) => setPrefs((p) => ({ ...p, whatsapp_summary_phone: e.target.value || null }))}
                onBlur={() => upsert({ whatsapp_summary_phone: prefs.whatsapp_summary_phone })}
              />
            </div>
          }
        />
        <ToggleRow
          label="Subscription Expiry Reminder"
          description="Get reminded before your plan expires"
          checked={prefs.subscription_reminder_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, subscription_reminder_enabled: v }))}
          onSave={(v) => upsert({ subscription_reminder_enabled: v })}
          children={
            <Select
              value={String(prefs.subscription_reminder_days)}
              onValueChange={(v) => {
                const n = parseInt(v, 10);
                setPrefs((p) => ({ ...p, subscription_reminder_days: n }));
                upsert({ subscription_reminder_days: n });
              }}
            >
              <SelectTrigger className="w-40 bg-background border-border">Remind me</SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days before</SelectItem>
                <SelectItem value="3">3 days before</SelectItem>
                <SelectItem value="1">1 day before</SelectItem>
              </SelectContent>
            </Select>
          }
        />
        <ToggleRow
          label="New Team Member Joined"
          description="Notify when someone accepts your invitation"
          checked={prefs.team_join_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, team_join_enabled: v }))}
          onSave={(v) => upsert({ team_join_enabled: v })}
        />
        <ToggleRow
          label="AI Analysis Ready"
          description="Notify when a BI analysis has completed"
          checked={prefs.ai_analysis_ready_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, ai_analysis_ready_enabled: v }))}
          onSave={(v) => upsert({ ai_analysis_ready_enabled: v })}
        />
        <ToggleRow
          label="Reorder Point Reached"
          description="Notify when a product hits its reorder threshold (Pro plans only)"
          checked={prefs.reorder_alert_enabled}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, reorder_alert_enabled: v }))}
          onSave={(v) => upsert({ reorder_alert_enabled: v })}
        />
      </div>
    </>
  );
}
